<template>
  <div id="app">
    <div class="page-wrapper">
      <div class="container">
        <h1 class="title">Privacy Policy</h1>
        <p>Last Updated: August 21, 2026</p>
        <p>
          Magicube Group Co., Limited (“we” or “our”) is committed to protecting
          the privacy of our users (“users” or “you”). This Privacy Policy
          (“Policy”) describes how we collect, store, use, and manage
          information, including personal information that you provide or that
          we collect through websites linked to this Policy (e.g.,
          https://cubetv.cc/privacy) or any mobile applications (such as the iOS
          and Android versions) (collectively referred to as the “Services”), as
          well as your privacy rights.
        </p>

        <p>
          Please note that this Policy applies only to the information we
          collect or receive through your use of the Services. It does not apply
          to the actions of third parties, the content of their websites, their
          use of information you provide to them, or any products or services
          they may offer, and we are not responsible for them. Any links to such
          websites do not constitute our sponsorship, endorsement, or
          affiliation with those individuals or companies.
        </p>

        <p>
          By using the Services, you acknowledge that you have read and
          understood this Policy and agree to the processing of your personal
          information as described herein.
        </p>

        <p>
          If you have any questions about this Policy or if your concerns are
          not addressed here, please contact us via email at:
          magicubegroup@gmail.com.
        </p>

        <p>Information We Collect From or About You</p>

        <p>
          We collect information about you as described below. As further
          detailed in the following sections, we use and, in certain cases,
          combine this information to operate, customize content, personalize,
          adjust, and improve our Services, as well as to send you messages or
          personalized information about our mobile applications, new features,
          and more—including promotional messages based on your preferences.
        </p>

        <p>
          Automatically Collected Information: When you interact with our
          services or use our services, we automatically collect certain
          information about your device and your usage of our services. This may
          include your browser type and language, IP address, device ID,
          hardware and software information, standard server log information,
          and purchase history, among other data. We (or third parties working
          with us) use standard tracking technologies (such as log files,
          software development kits (SDKs), and web beacons) to achieve this.
          Additionally, we or our partners may use tracking technologies and
          advertising IDs to deliver personalized ads to you within our
          services. Please refer to the section below about tracking
          technologies [insert link to Cookie and similar technologies section]
          for more details.
        </p>

        <p>
          Mobile Platforms: When you play or otherwise use any of our mobile
          applications, we or third-party partners may collect and process
          certain information about you, including your unique device ID
          (persistent/non-persistent), hardware type, device manufacturer, Media
          Access Control (“MAC”) address, International Mobile Equipment
          Identity (“IMEI”), the type and version of your operating system
          (“OS”), OS language, your unique device ID, your time zone, your
          device's screen size, mobile device advertising ID (referred to as
          “IDFA” for iOS devices and “Android Advertising ID” for Android
          devices), general geographic information (based on your Internet
          Protocol (“IP”) address city/country/region), and your email address
          (if you have connected to Facebook or Google+). This information helps
          us troubleshoot and better understand usage trends so that we can
          improve and personalize our user services. Device
          identifiers/information (such as device type, hardware model, device
          manufacturer, device operating system and its language, or other
          technical data such as screen size, or combinations of these data)
          will also be collected and used to determine device compatibility and
          optimal settings. We may also share this information with our
          advertising partners in order to deliver personalized ads to your
          device through technology, as outlined in the [link to interest-based
          ads section] below.
        </p>

        <p>
          We use tracking technologies to automatically collect information.
        </p>

        <p><strong>A. Tracking Technologies</strong></p>

        <p>
          We and our third-party partners, affiliates, or analytics service
          providers use standard tracking technologies (such as web beacons,
          SDKs, APIs, scripts, and tags) to identify your computer/device,
          "remember" information about your access to or use of the services
          (such as your preferences or username), and help us understand the
          pages and features you use, as well as the mobile applications you
          play.
        </p>

        <p>
          Such information may be linked or combined with other personal
          information we process about you (such as your user ID) to improve the
          quality of our services, tailor recommendations based on your
          interests, provide and personalize our services (including making our
          services more user-friendly), and for purposes such as fraud
          prevention, security, and authentication.
        </p>

        <p>
          If you would like assistance managing your preferences regarding the
          tracking technologies used in our services, please refer to the guides
          provided by your device manufacturer, operating system developer, or
          individual web browser.
        </p>

        <p><strong>B. Interest-based Advertising</strong></p>

        <p>
          Some of our services use advertising technology that employs cookies,
          clear GIFs, web beacons, tracking pixels, and other similar
          technologies to deliver interest-based ads about third-party products
          and services. We may promote our services to you through our
          third-party advertising partners on third-party websites. You may also
          see ads for our mobile applications on other services, such as
          Facebook. When you click on one of these ads and install our mobile
          application, you will become a user of our services.
        </p>

        <p>
          When you play our mobile applications or otherwise use our apps, some
          third-party tools (SDKs) integrated into our services may collect and
          use certain information from your device for ad display and
          optimization. This information includes GAID (Google Advertising ID on
          Android devices) or IDFA (Advertising Identifier on iOS devices),
          region (i.e., locations using specific languages), geographic
          information (with your consent), and IP address. This policy does not
          apply to our third-party advertising partners, as they have their own
          privacy policies, which you can access via the links provided below.
          We cannot access or control these third parties. In addition to
          identifiers, these third parties may collect information related to
          the following:
        </p>

        <ul>
          <li>Ads served and their placement;</li>
          <li>Duration of ad visibility;</li>
          <li>Domain type, IP address, ad size;</li>
          <li>
            Click-through rates (whether the ad was clicked) and ad responses.
          </li>
        </ul>

        <p>
          They use this information (either alone or combined with information
          collected about you when using third-party websites or services) to:
          (i) deliver ads to your device via technology and validate
          click-through rates and/or install rates; (ii) provide you with ads
          more relevant to our services (e.g., showing you offers for a mobile
          application you may like); (iii) enable offer walls, allowing you to
          choose from various incentivized ads or market research offers; (iv)
          track results and measure ad performance; and (v) improve the accuracy
          of their targeting and measurement systems. We recommend that you
          review the privacy policies of our advertising partners for more
          information. More details can be found below:
        </p>

        <ul>
          <li>
            Firebase:
            <a href="https://policies.google.com/privacy">https://policies.google.com/privacy</a>
          </li>
          <li>
            Facebook:
            <a href="https://www.facebook.com/privacy/policy">https://www.facebook.com/privacy/policy</a>
          </li>
          <li>
            Google:
            <a href="https://policies.google.com/privacy">https://policies.google.com/privacy</a>
          </li>
          <li>
            Appsflyer:
            <a href="https://www.appsflyer-gcr.cn/trust/privacy/">https://www.appsflyer-gcr.cn/trust/privacy/</a>
          </li>
        </ul>

        <p>
          You can learn more about advertising service companies and options for
          limiting their collection and use of your information by visiting the
          Network Advertising Initiative and Digital Advertising Alliance
          websites, as well as Facebook's Ad Preferences tool and Privacy Policy
          page. Similarly, you can opt out of certain ad networks' mobile app
          tracking by adjusting settings on your device or by resetting the
          advertising ID on your Apple or Android device. For more information
          on how to change these settings, please visit:
        </p>

        <ul>
          <li>
            Apple:
            <a href="http://support.apple.com/kb/HT4228">http://support.apple.com/kb/HT4228</a>
          </li>
          <li>
            Android:
            <a href="https://support.google.com/ads/answer/2662922">https://support.google.com/ads/answer/2662922</a>
          </li>
        </ul>

        <p>
          Note: Adjusting your preferences as described in this section does not
          mean you will no longer receive ads; it simply means that the ads you
          see may be less relevant to your interests. Our advertising partners
          will still use your device’s advertising ID to serve you less relevant
          ads; for example, we may use it to control the frequency (i.e., limit
          the maximum number of times you see the same ad). You can also reset
          the advertising ID associated with your device according to the
          instructions provided by your device manufacturer or operating system
          developer.
        </p>

        <p><strong>C. Analytics Technologies</strong></p>

        <p>
          We use our own servers and third-party analytics technologies to
          collect information about how you use our services, your computer or
          device, and the health of our services.
        </p>

        <p>
          For example, we use Google Analytics in our services, a web analytics
          service provided by Google, Inc. (1600 Amphitheatre Parkway, Mountain
          View, CA 94043, USA). Google Analytics uses cookies or other tracking
          technologies to help us analyze how users interact with and use the
          services, compile reports on service activity, and provide other
          services related to our service activities and usage. The technologies
          used by Google may collect information such as your IP address, access
          times, and whether you are a returning visitor. The services do not
          use Google Analytics to collect personally identifiable information.
          The information generated by Google Analytics is transmitted to Google
          and stored by them, subject to Google's privacy policy. To learn more
          about Google’s partner services and how to opt out of Google Analytics
          tracking, click
          <a href="https://www.google.com/policies/privacy/partners/">https://www.google.com/policies/privacy/partners/</a>.
        </p>

        <p><strong>How We Use Information</strong></p>

        <p>
          We use the information collected through our services (either alone or
          in combination) for the purposes described in this policy or related
          purposes associated with our services. For example, we may use your
          information for:
        </p>

        <ul>
          <li>Using our mobile applications;</li>
          <li>
            Operating, developing, and improving our mobile applications and
            other services;
          </li>
          <li>
            Understanding you and your preferences to enhance and personalize
            your experience and enjoyment of using our services;
          </li>
          <li>
            Providing you with personalized recommendations for the mobile
            applications and/or services;
          </li>
          <li>
            Responding to your comments and questions and providing customer
            service;
          </li>
          <li>
            Providing and delivering the products and services you request;
          </li>
        </ul>

        <ul>
          <li>Measuring the health of our services;</li>
          <li>
            Identifying, fixing, and troubleshooting errors and service or
            feature malfunctions;
          </li>
          <li>Serving ads and measuring their effectiveness;</li>
          <li>Personalizing ads for you;</li>
          <li>
            Personalizing the content and experience of our services, including
            providing reports, recommendations, and feedback based on your
            preferences;
          </li>
          <li>
            Optimizing or improving our products, services, and operations;
          </li>
          <li>
            Facilitating software updates and automatically updating the
            software on your device;
          </li>
          <li>
            Protecting the integrity of our services and enforcing our terms of
            service and other applicable terms, including detecting and
            preventing fraud and unauthorized activities, protecting our systems
            and services, preventing hacking, cheating, and spam, and generally
            keeping our services secure and fair, as well as detecting,
            investigating, and preventing activities that may violate our
            policies or be illegal;
          </li>
          <li>
            Conducting statistical, demographic, and marketing analysis of
            service users.
          </li>
        </ul>

        <p>
          We may use the information collected from you or about you to create
          aggregated or de-identified data that cannot specifically identify
          you.
        </p>

        <p><strong>How We Disclose Your Information</strong></p>

        <p>
          Unless we have your consent or as otherwise stated in this policy, we
          do not share your personal information. We may disclose your
          information in the following circumstances:
        </p>

        <ul>
          <li>
            <strong>With Your Consent:</strong> We may share your personal
            information with certain third parties after obtaining your approval
            and consent.
          </li>
          <li>
            <strong>Service Providers:</strong> We hire other companies and
            individuals to perform services on our behalf to fulfill the
            purposes described in this policy. Examples of such services include
            data analytics, IT and related services, payment processing, email
            sending, customer support, and fraud detection services. These
            agents and service providers may access your personal information
            while providing services to us and are contractually obligated to
            maintain the confidentiality and security of your personal
            information. We also require them to use the data solely on our
            behalf and for purposes consistent with this policy.
          </li>
          <li>
            <strong>Third-Party Plugins:</strong> Through third-party
            applications and platforms, such as Facebook Connect, you may choose
            to share information about your activities on our website and mobile
            applications on your profile page to share with others in your
            network. This policy does not apply to your use of such third-party
            applications and services, and we are not responsible for how these
            third parties collect, use, or disclose your information. We
            encourage you to review their privacy policies before connecting or
            using third-party applications or services to learn more about their
            information and privacy practices.
          </li>
          <li>
            <strong>Disclosures Made to Protect Us or Others:</strong> We may
            disclose your information in response to legal requirements, such as
            complying with subpoenas or when we believe disclosure is necessary
            to comply with the law; investigate fraud, respond to government
            requests, enforce or apply our rights; or protect the rights,
            property, and safety of ourselves, our users, or others. This
            includes exchanging information with other companies and
            organizations for fraud protection.
          </li>
          <li>
            <strong>Disclosures in Case of Merger, Sale, or Asset Transfer:</strong>
            We may share your information in the event of any merger, sale of
            our assets, or financing or if our business is fully or partially
            acquired by another company. We will notify you by email and/or
            through notifications on our services regarding any changes to the
            ownership or use of your personal information.
          </li>
          <li>
            <strong>Affiliated Companies:</strong> We may share personal
            information with our affiliated companies.
          </li>
          <li>
            <strong>Advertising Partners:</strong> Through our services, we may
            allow third-party advertising partners to set up technologies and
            other tracking tools to collect information about your use of the
            services and devices, as described in the interest-based advertising
            section above.
          </li>
        </ul>

        <p><strong>Other Important Information</strong></p>

        <p><strong>Policy Changes:</strong></p>

        <p>
          We may periodically update this policy to reflect changes in our
          information practices. If we make any significant changes, we will
          notify you before the changes take effect via email (sent to the email
          address specified in your account) or through notifications on the
          service. If applicable data protection laws require us to provide more
          detailed notice or obtain your consent for any such changes, we will
          do so. You can check the "Last Updated" date displayed at the top of
          this policy to see when this policy was last updated. We recommend
          that you review this page regularly for the latest information
          regarding our privacy practices.
        </p>

        <p><strong>Security</strong></p>
        <p>
          We take reasonable and appropriate measures to protect your
          information from unauthorized access, loss, misuse, or alteration by
          third parties.
        </p>

        <p>
          Although we make every effort to store the information collected
          through the service in a secure operating environment that is not
          publicly accessible, we cannot guarantee that the information is
          absolutely secure when transmitted or stored in our systems.
          Additionally, while we strive to maintain the integrity and security
          of our networks and systems, we cannot guarantee that our security
          measures will prevent third parties from obtaining this information
          unlawfully. We do not guarantee or represent that your information
          will be protected from loss, misuse, or alteration by third parties.
          However, no method of internet transmission or electronic storage is
          100% secure. Therefore, we cannot guarantee its absolute security.
        </p>

        <p><strong>Data Retention</strong></p>
        <p>
          We will retain your information for as long as necessary to provide
          you with the services. If you wish for us to stop using your
          information to provide services, please contact us at
          magicubegroup@gmail.com. Notwithstanding the above, we will retain and
          use your information as necessary to comply with our legal
          obligations, resolve disputes, and enforce our agreements.
        </p>

        <p><strong>Our Children’s Policy</strong></p>
        <p>
          We do not knowingly collect or solicit personal information from
          children under 13 years old (or the age of majority in your
          jurisdiction) nor knowingly allow children to use our services. If you
          are under 13 years old (or the age of majority in your jurisdiction),
          please do not send any information about yourself, including your
          name, address, phone number, or email address. No one under 13 should
          provide any personal information. If you believe we have collected
          personal information from a child, please contact us using the contact
          information below. If we learn that we have collected personal
          information from a child, we will delete that information as soon as
          commercially feasible.
        </p>

        <p><strong>International Transfers</strong></p>
        <p>
          We may transfer the information we collect about you across borders to
          affiliated entities or other third parties, and from your country or
          jurisdiction to other countries or jurisdictions around the world.
          Please note that these countries and regions may not have the same
          data protection laws as those in your country or region, and we take
          measures to ensure that adequate safeguards are in place to transfer
          information to the United States and elsewhere and to use and disclose
          your information, including personal information, as described in this
          policy.
        </p>

        <p><strong>Your Privacy Choices</strong></p>

        <p><strong>Your Choice Regarding Information You Provide</strong></p>
        <p>
          If you do not wish to provide us with personal information, you can
          choose not to provide it. If you choose not to provide us with
          personal information, we may not always be able to provide the
          services you desire.
        </p>

        <p>
          <strong>Your Choice Regarding Information Provided to Third Parties</strong>
        </p>
        <p>
          You can control how third parties collect and share your information
          by changing your preferences or choosing not to interact with third
          parties who collect information directly. We recommend that you review
          their respective privacy policies to learn more before disclosing
          information to third parties.
        </p>
        <p><strong>Accessing and Deleting Your Information</strong></p>
        <p>
          You can contact us at magicubegroup@gmail.com to deactivate your
          account or delete the email associated with your account.
        </p>

        <p>
          If you would like to access or view the personal information we hold
          about you, or if for any reason you are concerned about how we use
          your personal information, or would like to correct or request that we
          delete such personal information, you can also send your request to
          magicubegroup@gmail.com.
        </p>

        <p>
          If you are a resident of California or a user from the European
          Economic Area ("EEA"), the United Kingdom, or Switzerland, and want to
          learn more about your personal information rights, please refer to the
          relevant sections below.
        </p>

        <p>
          <strong>Supplemental Information for California Residents</strong>
        </p>
        <p>
          If you are a resident of California, the following information applies
          to you and supplements the information regarding your use of our
          services as outlined in this policy.
        </p>

        <p>
          <strong>Information We Collect and Sources of Information:</strong>
        </p>
        <p>
          Our policy has described the information we collect and the sources of
          information—please refer to the "Information We Collect from You or
          About You" section above. This section organizes this information
          according to the personal information categories specified in the
          California Consumer Privacy Act ("CCPA").
        </p>

        <p>
          We have collected the following information for the following
          purposes:
        </p>
        <ul>
          <li>
            <strong>Identifiers:</strong> For example, internet protocol
            address, unique personal identifiers, account username, or screen
            name. We obtain this information when you provide it to us or
            automatically as you interact with our services.
          </li>
          <li>
            <strong>Internet or Other Electronic Network Activity
              Information:</strong>
            For example, browser type and language, device and hardware
            information, game data, and other information related to your
            interactions with our services. We automatically collect this
            information as you interact with our services.
          </li>
          <li>
            <strong>Demographic Information/Protected Characteristics:</strong>
            For example, age and gender. We obtain this information when you
            provide it to us, or automatically during your interactions with our
            services.
          </li>
          <li>
            <strong>Commercial Information:</strong> This includes records of
            products purchased or considered, or other similar consumption
            histories or tendencies. This allows us to provide technical
            features, develop new products and services, and analyze your usage
            of them. We collect this information directly from you or
            automatically as you interact with our services.
          </li>
          <li>
            <strong>Geolocation Data:</strong> This includes, for example, your
            region settings, postal code, and general location information
            (based on your IP address). We collect this information when you
            provide it to us or automatically as you interact with our services.
          </li>
          <li>
            <strong>Profiles and Inferences:</strong> This includes profiles
            created from any of the above information to reflect preferences,
            characteristics, tendencies, behaviors, attitudes, intelligence,
            abilities, and skills. We automatically collect this information as
            you interact with our services.
          </li>
        </ul>

        <p>
          We directly collect personal data from you, automatically collect it
          when you use our services, collect it from non-affiliated parties (for
          example, when you use social login features to interact with us), and
          acquire additional personal data about you by using or combining
          personal data as described in the "Personal Data We Collect" section
          above.
        </p>

        <p><strong>How We Use and Share Information</strong></p>
        <p>
          We collect and use personal information for business and commercial
          purposes, as described in the "How We Use Information" section above.
        </p>

        <p>
          Unless you provide your consent or unless otherwise specified in this
          policy, we do not share your personal information. You can find more
          information in the "How We Disclose Your Information" section above.
          We have disclosed the following categories of personal information for
          business purposes:
        </p>
        <ul>
          <li><strong>Identifiers:</strong> As described above.</li>
          <li>
            <strong>Internet or Other Electronic Network Activity
              Information:</strong>
            As described above.
          </li>
          <li>
            <strong>Demographic Information/Protected Characteristics:</strong>
            As described above.
          </li>
          <li><strong>Commercial Information:</strong> As described above.</li>
          <li><strong>Geolocation Data:</strong> As described above.</li>
          <li><strong>Profiles and Inferences:</strong> As described above.</li>
        </ul>

        <p>
          We do not sell personal information about users. However, we allow
          certain third-party advertising and analytics partners to collect
          information about consumers through our services for the purpose of
          delivering more relevant ads, evaluating and analyzing advertising
          campaigns, and performing ad fraud detection and reporting. The
          information collected by our partners includes GAID (Google
          Advertising ID on Android devices) or IDFA (Identifier for Advertisers
          on iOS devices), region (i.e., regions with specific language usage),
          geolocation data (with your consent), and IP addresses. To learn more
          or opt-out of using your data for advertising purposes, please refer
          to the "Interest-Based Advertising" section above.
        </p>

        <p><strong>Your Privacy Rights</strong></p>
        <p>Under the CCPA, California residents have the following rights:</p>
        <ul>
          <li>
            <strong>Right to Know:</strong> You have the right to request that
            we disclose the personal information we have collected, used, and
            shared in the past 12 months, as well as information about our data
            practices.
          </li>
          <li>
            <strong>Right to Request Deletion:</strong> You have the right to
            request that we delete the personal information we have collected
            from you.
          </li>
          <li>
            <strong>Right to Non-Discrimination:</strong> We will not
            discriminate against you for exercising any of these rights.
          </li>
          <li>
            <strong>Right to Opt-Out of the Sale of Personal Information:</strong>
            We do not sell personal information, so we do not have an opt-out
            right.
          </li>
          <li>
            <strong>Right to Financial Incentive Notifications:</strong> You
            have the right to be informed of any financial incentive offers and
            their key terms, and the right to opt-out of such incentives at any
            time. We currently do not offer any such incentives.
          </li>
        </ul>

        <p>
          California law also allows you to request certain information
          regarding our and our affiliates' disclosures of your personal
          information to third parties for direct marketing purposes. We do not
          disclose your personal information to third parties for their direct
          marketing purposes.
        </p>

        <p>
          As there is currently no consensus on how to interpret "Do Not Track"
          signals, we do not respond to "Do Not Track" signals. However, we do
          provide you with the option to opt-out of the use of tracking
          technologies to send you interest-based advertising, as described
          above.
        </p>

        <p>
          To exercise any of the rights mentioned above, or if you are an
          authorized agent acting under the CCPA to exercise rights on behalf of
          a California resident, you can email us at magicubegroup@gmail.com or
          contact us at the address below.
        </p>

        <p>
          Please note that to protect your information, we may need to verify
          your identity before processing your request. In some cases, we may
          need to collect additional information to verify your identity, such
          as government-issued identification.
        </p>

        <p>
          Under the CCPA, you may exercise these rights yourself or authorize an
          agent to do so on your behalf. We may require your written consent for
          an authorized agent to submit a request on your behalf, and may need
          to verify the agent's identity.
        </p>

        <p>
          If you would like to learn more about our privacy practices, you can
          email us at magicubegroup@gmail.com.
        </p>

        <p>
          <strong>Additional Information for Users in the European Economic Area
            (EEA), United Kingdom, and Switzerland</strong>
        </p>

        <p>
          If you are a resident of the EEA, United Kingdom, or Switzerland, the
          following information applies to your personal data collected when you
          use our services.
        </p>

        <p>
          <strong>Data Controller</strong><br>
          Magicube Group Co., Limited (Registered Address: RM 1801 EASEY COMM
          BLDG 253-261 HENNESSY ROAD WANCHAI HK.) is the data controller of
          your personal data.
        </p>

        <p>
          <strong>Legal Basis for Processing Your Personal Data</strong><br>
          We collect, use, and share your personal data based on the following
          principles:
        </p>

        <ul>
          <li>
            <strong>Consent:</strong> We process your personal data based on
            your consent. This includes, for example, providing:
            <ul>
              <li>
                Electronic communications about our services, direct emails, and
                surveys;
              </li>
              <li>Targeted advertising in our mobile applications;</li>
              <li>Other marketing features.</li>
            </ul>
          </li>
          <li>
            <strong>Legitimate Interests:</strong> We process your personal data
            based on legitimate interests, provided these interests do not
            override your fundamental rights and freedoms related to data
            privacy. This includes, for example:
            <ul>
              <li>
                Providing the customer service or technical support you need;
              </li>
              <li>Debugging and improving our current and future services;</li>
              <li>Contacting you based on applicable marketing preferences;</li>
              <li>
                Establishing, exercising, or defending legal claims, or when a
                court exercises its judicial authority.
              </li>
            </ul>
          </li>
          <li>
            <strong>Legal Obligation:</strong> We also process your personal
            data to comply with our legal obligations.
          </li>
        </ul>

        <p>
          <strong>International Data Transfers</strong><br>
          The personal data we collect may be transferred to the United States
          or any other country/region where we or our affiliates, partners, or
          service providers maintain facilities or business operations, where it
          will be stored and processed. We will ensure that personal data is
          appropriately protected when transferred to third countries or
          international organizations.
        </p>

        <p>
          <strong>Your Privacy Rights</strong><br>
          You have the following rights regarding your personal data:
        </p>

        <ul>
          <li>
            <strong>Access, Rectification, and Deletion Rights:</strong> You
            have the right to view, correct, update, or request the deletion of
            your personal data as described in the "Access and Delete Your
            Information" section above.
          </li>
          <li>
            <strong>Right to Object:</strong> If we process your personal data
            based on legitimate interests, you may object to the processing of
            your personal data for reasons related to your specific situation.
            For example, you can control the level of marketing we send to you,
            and you have the right to request that we stop sending marketing
            communications at any time. You can use the methods described in the
            "Your Choices" section above.
          </li>
          <li>
            <strong>Right to Withdraw Consent:</strong> If we process your
            personal data based on your consent, you have the right to withdraw
            your consent to further use of your personal data at any time. You
            can update your marketing preferences using the methods described in
            the "Your Choices" section above.
          </li>
          <li>
            <strong>Right to Data Portability:</strong> In certain cases, you
            may request that we transfer or transmit your personal data to
            another organization.
          </li>
        </ul>

        <p>
          For each of the rights above, you can send your request to
          magicubegroup@gmail.com or use the contact information listed below.
        </p>

        <p>
          We hope to address any concerns you may have about how we handle your
          personal data. However, if you have unresolved concerns, you also have
          the right to lodge a complaint with the relevant data protection
          authority. You can file a complaint with the authority in your country
          of residence, your place of work, or where you believe the data
          protection law has been violated. For more information, please contact
          your local data protection authority. For contact details of European
          Economic Area and Swiss data protection authorities, click here.
        </p>
        <p><strong>Contact Information</strong></p>

        <p>If you have any questions about this policy, please contact us:</p>

        <p><strong>MAGICUBE GROUP CO., LIMITED</strong><br>
          Registered Address: RM 1801 EASEY COMM BLDG 253-261 HENNESSY ROAD WANCHAI HK.<br>
          Email: <a href="mailto:magicubegroup@gmail.com">magicubegroup@gmail.com</a></p>

      </div>
    </div>
  </div>
</template>

<script lang="js">
export default {
  data() {
    return {

    }
  },
}
</script>

<style scoped lang="scss">
#app {
  background: #fff;
  padding: 80px 40px;
  min-height: 100vh;
  box-sizing: border-box;
  font-family: 'Helvetica Neue', Arial, sans-serif;
}

.page-wrapper {
  display: flex;
  justify-content: center;
}

.container {
  max-width: 960px;
  width: 100%;
}

.title {
  font-size: 36px;
  font-weight: bold;
  color: #000;
  text-align: center;
  margin-bottom: 40px;
}

h2 {
  font-size: 24px;
  font-weight: 600;
  color: #000;
  margin: 32px 0 16px;
}

p,
li {
  color: #333;
  font-size: 16px;
  line-height: 1.8;
}

ul {
  padding-left: 24px;
  margin: 12px 0;
}

strong {
  color: #000;
}
</style>
